export const MIN_ASPECT_RATIO = 3 / 4;
export const MAX_ASPECT_RATIO = 16 / 9;
export const GRID_GAP = 8; // pixels between tiles
export const MIN_TILE_WIDTH = 240;

export const PARTICIPANT_ROLE = {
  MODERATOR: 'moderator',
  NONE: 'none',
} as const;

export const MEDIA_TYPE = {
  AUDIO: 'audio',
  VIDEO: 'video',
  SCREENSHARE: 'screenshare',
} as const;

export const LAYOUT = {
  TILE: 'TILE',
  SIDEBAR: 'SIDEBAR',
  SPOTLIGHT: 'SPOTLIGHT',
} as const;

export const SESSION_TYPE = {
  VOICE: 'VOICE',
  VIDEO: 'VIDEO',
} as const;

export const CAMERA_FACING = {
  FRONT: 'FRONT',
  REAR: 'REAR',
} as const;

export const CAMERA_FACING_INTERNAL = {
  USER: 'user',
  ENVIRONMENT: 'environment',
} as const;

export const VIDEO_PAUSE_AUTHORITY = {
  USER: 1 << 0,
  BACKGROUND: 1 << 1,
  SCREEN_SHARE: 1 << 2,
};

export const CONFERENCE_COMMANDS = {
  userInfo: 'user-info',
};

export const VIDEO_QUALITY_LEVELS = {
  ULTRA: 2160,
  HIGH: 720,
  STANDARD: 360,
  LOW: 180,
  NONE: 0,
};

export const PLATFORM = {
  WEB: 'web',
  ANDROID: 'android',
  IOS: 'ios',
} as const;

export const EVENT_LISTENER_METHODS = {
  SessionStatusListener: {
    onSessionJoined: 'onSessionJoined',
    onSessionLeft: 'onSessionLeft',
    onConnectionLost: 'onConnectionLost',
    onConnectionRestored: 'onConnectionRestored',
    onConnectionClosed: 'onConnectionClosed',
    onSessionTimedOut: 'onSessionTimedOut',
  },
  MediaEventsListener: {
    onAudioMuted: 'onAudioMuted',
    onAudioUnMuted: 'onAudioUnMuted',
    onVideoPaused: 'onVideoPaused',
    onVideoResumed: 'onVideoResumed',
    onRecordingStarted: 'onRecordingStarted',
    onRecordingStopped: 'onRecordingStopped',
    onScreenShareStarted: 'onScreenShareStarted',
    onScreenShareStopped: 'onScreenShareStopped',
  },
  ParticipantEventsListner: {
    onParticipantJoined: 'onParticipantJoined',
    onParticipantLeft: 'onParticipantLeft',
    onParticipantAudioMuted: 'onParticipantAudioMuted',
    onParticipantAudioUnmuted: 'onParticipantAudioUnmuted',
    onParticipantVideoPaused: 'onParticipantVideoPaused',
    onParticipantVideoResumed: 'onParticipantVideoResumed',
    onParticipantHandRaised: 'onParticipantHandRaised',
    onParticipantHandLowered: 'onParticipantHandLowered',
    onParticipantStartedScreenShare: 'onParticipantStartedScreenShare',
    onParticipantStoppedScreenShare: 'onParticipantStoppedScreenShare',
    onParticipantStartedRecording: 'onParticipantStartedRecording',
    onParticipantStoppedRecording: 'onParticipantStoppedRecording',
    onDominantSpeakerChanged: 'onDominantSpeakerChanged',
    onParticipantListChanged: 'onParticipantListChanged',
  },
  ButtonClickListener: {
    onLeaveSessionButtonClicked: 'onLeaveSessionButtonClicked',
    onRaiseHandButtonClicked: 'onRaiseHandButtonClicked',
    onShareInviteButtonClicked: 'onShareInviteButtonClicked',
    onChangeLayoutButtonClicked: 'onChangeLayoutButtonClicked',
    onParticipantListButtonClicked: 'onParticipantListButtonClicked',
    onToggleAudioButtonClicked: 'onToggleAudioButtonClicked',
    onToggleVideoButtonClicked: 'onToggleVideoButtonClicked',
    onRecordingToggleButtonClicked: 'onRecordingToggleButtonClicked',
    onScreenShareButtonClicked: 'onScreenShareButtonClicked',
    onChatButtonClicked: 'onChatButtonClicked',
  },
  CallLayoutListener: {
    onCallLayoutChanged: 'onCallLayoutChanged',
    onParticipantListVisible: 'onParticipantListVisible',
    onParticipantListHidden: 'onParticipantListHidden',
  },
} as const;

export const EVENT_LISTENER_METHODS_WEB = {
  ...EVENT_LISTENER_METHODS,
  MediaEventsListener: {
    ...EVENT_LISTENER_METHODS.MediaEventsListener,
    onAudioInputDeviceChanged: 'onAudioInputDeviceChanged',
    onVideoInputDeviceChanged: 'onVideoInputDeviceChanged',
    onAudioOutputDeviceChanged: 'onAudioOutputDeviceChanged',
    onAudioInputDevicesChanged: 'onAudioInputDevicesChanged',
    onVideoInputDevicesChanged: 'onVideoInputDevicesChanged',
    onAudioOutputDevicesChanged: 'onAudioOutputDevicesChanged',
  },
} as const;

export const EVENT_LISTENER_METHODS_MOBILE = {
  ...EVENT_LISTENER_METHODS,
  MediaEventsListener: {
    ...EVENT_LISTENER_METHODS.MediaEventsListener,
    onAudioModeChanged: 'onAudioModeChanged',
    onCameraFacingChanged: 'onCameraFacingChanged',
  },
  ButtonClickListener: {
    ...EVENT_LISTENER_METHODS.ButtonClickListener,
    onSwitchCameraButtonClicked: 'onSwitchCameraButtonClicked',
  },
  CallLayoutListener: {
    ...EVENT_LISTENER_METHODS.CallLayoutListener,
    onPictureInPictureLayoutEnabled: 'onPictureInPictureLayoutEnabled',
    onPictureInPictureLayoutDisabled: 'onPictureInPictureLayoutDisabled',
  },
} as const;

export const INTERNAL_EVENTS = {
  connectionWillClose: 'internal.sessionWillClose',
  onConnectionEstablished: 'internal.onConnectionEstablished',
  onSessionJoined: 'internal.onSessionJoined',
  notification: 'internal.notification',
  queryPermission: 'internal.queryPermission',
  lifecycle: {
    componentDidMount: 'internal.componentDidMount',
    componentWillUnmount: 'internal.componentWillUnmount',
    SDKInitialized: 'internal.SDKInitialized',
  },
} as const;
